import { Component, Input, Output, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { CLASSIFICATION_OPTIONS } from '../../../../../ordnances';
import { InputComponent } from '../../../shared/input/input.component';
import { InputMockComponent } from '../../../shared/input-mock/input-mock.component';
import { RowComponent } from '../../../shared/row/row.component';
import { OrdnanceSearchModalComponent } from '../ordnance-search-modal/ordnance-search-modal.component';
import { ImageUploadDropzoneComponent } from '../../../shared/image-upload-dropzone/image-upload-dropzone.component';

import { Image, ImageTag } from '../../../model/hero-report-image/image.model';
import { ReportType } from '../../../utilities/ordnanceCategories';
import { linkLocationControls } from '../../../utilities/location';

import { ErrorMessageComponent } from '../../../shared/error-message/error-message.component';
import { v4 } from 'uuid';

interface IdentificationItem {
  id: string;
  key?: string;
  label?: string;
}

@Component({
  selector: 'app-item-form',
  standalone: true,
  imports: [
    CommonModule,
    InputComponent,
    InputMockComponent,
    RowComponent,
    OrdnanceSearchModalComponent,
    ReactiveFormsModule,
    ImageUploadDropzoneComponent,
    ErrorMessageComponent,
  ],
  templateUrl: './item-form.component.html',
  styleUrl: './item-form.component.scss',
})
export class ItemFormComponent implements OnInit, OnDestroy {
  public ImageTag = ImageTag;

  @Input() formGroup!: FormGroup;

  @Input() overallImages: Image[] = [];
  @Input() noseImages: Image[] = [];
  @Input() tailImages: Image[] = [];

  @Input() deleteable = false;

  /**
   * Shows the MGRS, which the form group then has to have controls for, with the latitude and longitude it sets
   * (see {@link linkLocationControls}). UXO reports have it; SRE reports have an MGRS of their own in the details.
   */
  @Input() showLocation = false;

  /** SRE reports pick from the SRE items only; UXO (the default) from everything else. */
  @Input() reportType: ReportType = 'UXO';

  @Output() classificationSelect = new EventEmitter<any>();
  @Output() imagesAdd = new EventEmitter<Image>();
  @Output() imagesDelete = new EventEmitter<Image>();

  classificationOptions = CLASSIFICATION_OPTIONS.map((option) => ({
    label: option.label + ' ' + option.size,
    value: option.key,
  }));

  classificationLabel = 'Unknown';

  showOrdnanceModal = false;

  private editingItemId: string | null = null;

  identificationItems: IdentificationItem[] = [];

  private locationSubscriptions: Subscription[] = [];

  ngOnInit() {
    if (this.showLocation) {
      this.locationSubscriptions = linkLocationControls(this.formGroup);
    }

    this.identificationItems = (
      this.formGroup.get('possibleIdentification')?.value || []
    )
      .filter((key: string) => key.trim() !== '')
      .map((key: string) => {
        const matched = CLASSIFICATION_OPTIONS.find(
          (o: any) => o.key === key.trim(),
        );
        return {
          id: v4(),
          key: key.trim(),
          label: matched ? matched.label + ' ' + matched.size : key.trim(),
        };
      });

    if (this.identificationItems.length === 0) {
      this.identificationItems = [{ id: v4() }];
    }
  }

  ngOnDestroy() {
    this.locationSubscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  onClassificationSelect(event: any) {
    this.classificationSelect.emit(event);
  }

  onPossibleIdentificationClick() {
    this.editingItemId = null;
    this.showOrdnanceModal = true;
  }

  addIdentificationItem() {
    this.identificationItems.push({ id: v4() });
  }

  editIdentification(itemId: string) {
    this.editingItemId = itemId;
    this.showOrdnanceModal = true;
  }

  removeIdentification(itemId: string) {
    this.identificationItems = this.identificationItems.filter(
      (i) => i.id !== itemId,
    );

    const keys = this.identificationItems.map((i) => i.key).filter(Boolean);
    // A list, like onOrdnanceSelect sets; text here was saved as the one string "A,B".
    this.formGroup.get('possibleIdentification')?.setValue(keys);
  }

  onOrdnanceModalClose() {
    this.showOrdnanceModal = false;
  }

  onOrdnanceSelect(selection: { key: string; data: any }) {
    const matched = CLASSIFICATION_OPTIONS.find(
      (o: any) => o.key === selection.key,
    );
    const label = matched ? matched.label + ' ' + matched.size : selection.key;

    // replace existing
    this.identificationItems = this.identificationItems.map((it) =>
      it.id === this.editingItemId ? { ...it, key: selection.key, label } : it,
    );

    // Update the form control to keep backward compatibility with other components
    const keys = this.identificationItems.map((i) => i.key).filter(Boolean);
    // store the first key in the existing form control so other code that
    // expects a single key continues to work; we still expose allSelections in the event
    this.formGroup.get('possibleIdentification')?.setValue(keys);

    // Also set evacuation perimeter from the selection (use the last selected item's radius)
    if (selection.data) {
      this.formGroup
        .get('evacuationPerimeterM')
        ?.setValue(selection.data.evacuationRadius);
    }

    this.showOrdnanceModal = false;

    this.classificationSelect.emit({
      value: keys,
      evacuationRadius: selection.data?.evacuationRadius,
    });
  }

  getPossibleIdentificationLabel(item: IdentificationItem): string {
    const matched = CLASSIFICATION_OPTIONS.find(
      (ord: any) => ord.key === item.key,
    );
    return matched ? matched.label + ' ' + matched.size : null;
  }
}
